package com.gastoncantera.intercom.push

import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.await
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import kotlin.js.Date
import kotlin.js.Promise

/**
 * En web la notificación la muestra/oculta el service worker aunque la página esté cerrada, así que
 * las perdidas se persisten en IndexedDB (ver notifications-db.js / firebase-messaging-sw.js). Acá
 * las re-hidratamos a memoria al abrir/enfocar la app y cuando el SW avisa que cambiaron.
 */
actual object MissedCalls {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var refreshGeneration = 0
    private var clearing = false

    init {
        val setListener = window.asDynamic().intercomSetMissedListener
        if (setListener != null) setListener({ refresh() })
        refresh()
    }

    actual val popupTick: StateFlow<Int> = MissedCallStore.tick

    actual fun pending(): List<MissedCall> = MissedCallStore.pending()

    actual suspend fun clear() {
        clearing = true
        refreshGeneration++
        try {
            val clearer = window.asDynamic().intercomClearMissedCalls
            if (clearer != null) (clearer() as Promise<*>).await()
            MissedCallStore.clear()
        } catch (_: Throwable) {
            MissedCallStore.clear()
        } finally {
            clearing = false
        }
    }

    actual fun onForeground() {
        refresh()
    }

    private fun refresh() {
        if (clearing) return
        val getter = window.asDynamic().intercomPendingMissedCalls ?: return
        val generation = ++refreshGeneration
        scope.launch {
            try {
                val entries = (getter() as Promise<Array<dynamic>>).await()
                if (generation != refreshGeneration) return@launch
                MissedCallStore.replaceAll(
                    entries.map { entry ->
                        val callId = (entry.callId as? String) ?: ""
                        val at = (entry.at as? Number)?.toDouble() ?: 0.0
                        val (title, subtitle) = missedCallLabel(callId)
                        MissedCall(title, subtitle, Date(at).toLocaleString())
                    },
                )
            } catch (_: Throwable) {
                // Sin IndexedDB o acceso bloqueado: dejamos la lista como está.
            }
        }
    }
}

actual fun onIncomingCallNotificationShown(title: String, body: String) = Unit

actual fun onIncomingCallNotificationDismissed() = Unit

actual fun onIncomingCallNotificationRejected() = Unit

actual fun notifyIncomingCallHandledByUser(callId: String?) = Unit
